import React, { useState, useEffect } from 'react';
import { Award, Handshake, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners`);
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading partners...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-50 to-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-red-600">Partners & Sponsors</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            We're proud to work with leading organizations to bring life-saving training to our community.
          </p>
        </div>
      </section>

      {/* Certification Partner */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Award className="text-red-600 mx-auto mb-6" size={64} />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              American Heart Association Training Network
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              PUSH Training - NC is aligned with the American Heart Association, the world leader in resuscitation science and education. All our certifications meet AHA standards and are recognized nationwide.
            </p>
          </div>
        </div>
      </section>

      {/* Partners Grid */}
      {partners.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Community Partners
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {partners.map((partner) => (
                <Card key={partner.id} className="border-2 hover:shadow-lg transition-all text-center">
                  <CardHeader>
                    <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                      <img 
                        src={partner.logo} 
                        alt={partner.name}
                        className="max-w-full max-h-full object-contain p-4"
                      />
                    </div>
                    <CardTitle className="text-xl">{partner.name}</CardTitle>
                    <CardDescription className="text-base">{partner.type}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recommendations Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Trusted By
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-2">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="text-red-600" size={28} />
                </div>
                <CardTitle className="text-2xl">Healthcare Facilities</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  We partner with local hospitals, clinics, and healthcare organizations to provide on-site training for their staff, ensuring compliance with certification requirements.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <Handshake className="text-red-600" size={28} />
                </div>
                <CardTitle className="text-2xl">Educational Institutions</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Schools, daycare centers, and universities trust PUSH Training - NC to prepare their staff and students with essential life-saving skills.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Become a Partner CTA */}
      <section className="py-16 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Interested in Partnering With Us?
          </h2>
          <p className="text-xl text-red-100 mb-8">
            We're always looking to collaborate with organizations committed to community safety and wellness.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:certs@pushtrainingnc.com" className="inline-block">
              <button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-3 rounded-md font-semibold text-lg transition-colors">
                Contact Us About Partnership
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
